import React from 'react';
import { footerData } from '../../data/footerData';
import {
  FaFacebook,
  FaInstagram,
  FaEnvelope,
  FaPhone,
  FaTwitter,
  FaLinkedin,
  FaWhatsapp,
} from 'react-icons/fa';
import { IconBaseProps } from 'react-icons';
import JideOyelayo from '../Common/JideOyelayo';
import { trackEvent } from '../../firebase/analytics';

const FacebookIcon = (props: IconBaseProps) =>
  React.createElement(FaFacebook as any, props);
const InstagramIcon = (props: IconBaseProps) =>
  React.createElement(FaInstagram as any, props);
const MailIcon = (props: IconBaseProps) =>
  React.createElement(FaEnvelope as any, props);
const PhoneIcon = (props: IconBaseProps) =>
  React.createElement(FaPhone as any, props);
const TwitterIcon = (props: IconBaseProps) =>
  React.createElement(FaTwitter as any, props);
const LinkedinIcon = (props: IconBaseProps) =>
  React.createElement(FaLinkedin as any, props);
const WhatsAppIcon = (props: IconBaseProps) =>
  React.createElement(FaWhatsapp as any, props);

// Map social IDs to wrapper icon components
const iconMap: Record<string, React.FC<{ className?: string }>> = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  mail: MailIcon,
  phone: PhoneIcon,
  twitter: TwitterIcon,
  linkedin: LinkedinIcon,
  whatsapp: WhatsAppIcon,
};

// Helper to fire GA4/Firebase "select_content" for footer clicks
const makeFooterClick = (
  label: string,
  href: string,
  type: 'nav' | 'contact' | 'social'
) => () => {
  trackEvent({
    name: 'select_content',
    action: 'click',
    label,
    page: typeof window !== 'undefined' ? window.location.pathname : undefined,
    params: {
      destination: href,
      footer_type: type,
    },
  });
};

export const Footer: React.FC = () => {
  return (
    <footer className="bg-gray-800 text-white py-12">
      <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
        {/* Navigation Sections */}
        {footerData.sections.map((section) => (
          <div key={section.id}>
            <h4 className="font-semibold text-lg mb-4">{section.title}</h4>
            <ul className="space-y-2">
              {section.links.map((link) => (
                <li key={link.id}>
                  <a
                    href={link.href}
                    onClick={makeFooterClick(link.label, link.href, 'nav')}
                    data-analytics={`footer-nav-${link.id}`}
                    className="text-gray-400 hover:text-white transition"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Contact Us Section */}
        <div>
          <h4 className="font-semibold text-lg mb-4">Contact Us</h4>
          <ul className="space-y-2 text-gray-400">
            <li className="flex items-center">
              {React.createElement(FaEnvelope as any, { className: 'mr-2 text-xl' })}
              <a
                href="mailto:Admin@greatchild.co.uk"
                onClick={makeFooterClick('email', 'mailto:Admin@greatchild.co.uk', 'contact')}
                data-analytics="footer-contact-email"
                className="hover:text-white transition"
              >
                Admin@greatchild.co.uk
              </a>
            </li>
            <li className="flex items-center">
              {React.createElement(FaPhone as any, { className: 'mr-2 text-xl' })}
              <a
                href="tel:+447983133002"
                onClick={makeFooterClick('phone', 'tel:+447983133002', 'contact')}
                data-analytics="footer-contact-phone"
                className="hover:text-white transition"
              >
                +44 7983 133002
              </a>
            </li>
          </ul>
        </div>

        {/* Social Links */}
        <div>
          <h4 className="font-semibold text-lg mb-4">Follow Us</h4>
          <div className="flex space-x-4 text-2xl">
            {footerData.socialLinks.map((social) => {
              const IconComponent = iconMap[social.id] || FacebookIcon;
              return (
                <a
                  key={social.id}
                  href={social.href}
                  aria-label={social.ariaLabel}
                  onClick={makeFooterClick(social.id, social.href, 'social')}
                  data-analytics={`footer-social-${social.id}`}
                  className="text-gray-400 hover:text-white transition"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {React.createElement(IconComponent, null)}
                </a>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-12 text-center text-gray-500 text-sm">
        &copy; {new Date().getFullYear()} Great Child. All rights reserved.
      </div>

      <JideOyelayo />
    </footer>
  );
};

export default Footer;